import React, { useState, useRef, FormEvent, ChangeEvent } from 'react';
import emailjs from '@emailjs/browser';

import langIt from '../lang/lang.it.json';
import langEn from '../lang/lang.en.json';

// EmailJS Configuration - Replace these with your actual values
const EMAILJS_SERVICE_ID = 'service_jy0ohyv'; // Replace with your EmailJS service ID
const EMAILJS_TEMPLATE_ID = 'template_epm706y'; // Replace with your EmailJS template ID
const EMAILJS_PUBLIC_KEY = 'Bift_LMPjAF4WoOwr'; // Replace with your EmailJS public key

// Cloudinary Configuration
const CLOUDINARY_CLOUD_NAME = 'djacfg5gg';
const CLOUDINARY_UPLOAD_PRESET = 'CLOUDINARY_UPLOAD_PRESET'; // Create this unsigned preset in Cloudinary Settings

interface FormData {
  name: string;
  surname: string;
  dateOfBirth: string;
  cityOfResidency: string;
  sportCv: File | null;
  prospettive: string;
  email: string;
  phone: string;
}

interface FormErrors {
  name?: string;
  surname?: string;
  dateOfBirth?: string;
  cityOfResidency?: string;
  sportCv?: string;
  prospettive?: string;
  email?: string;
  phone?: string;
}

interface ContactFormProps {
  language: 'it' | 'en';
}

const ContactForm: React.FC<ContactFormProps> = ({ language }) => {
  const formTexts = language === 'en' ? langEn.form : langIt.form;
  const formRef = useRef<HTMLFormElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [formData, setFormData] = useState<FormData>({
    name: '',
    surname: '',
    dateOfBirth: '',
    cityOfResidency: '',
    sportCv: null,
    prospettive: '',
    email: '',
    phone: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [fileName, setFileName] = useState<string>('');

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    const phoneRegex = /^[\d\s+\-()]{7,20}$/;
    return phoneRegex.test(phone);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = formTexts.validation.nameRequired;
    }

    if (!formData.surname.trim()) {
      newErrors.surname = formTexts.validation.surnameRequired;
    }

    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = formTexts.validation.dateOfBirthRequired;
    }

    if (!formData.cityOfResidency.trim()) {
      newErrors.cityOfResidency = formTexts.validation.cityOfResidencyRequired;
    }

    if (!formData.sportCv) {
      newErrors.sportCv = formTexts.validation.sportCvRequired;
    }

    if (!formData.prospettive.trim()) {
      newErrors.prospettive = formTexts.validation.required;
    }

    if (!formData.email.trim()) {
      newErrors.email = formTexts.validation.required;
    } else if (!validateEmail(formData.email)) {
      newErrors.email = formTexts.validation.invalidEmail;
    }

    if (!formData.phone.trim()) {
      newErrors.phone = formTexts.validation.required;
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = formTexts.validation.invalidPhone;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setErrors(prev => ({
          ...prev,
          sportCv: formTexts.validation.pdfOnly
        }));
        setFormData(prev => ({
          ...prev,
          sportCv: null
        }));
        setFileName('');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      setFormData(prev => ({
        ...prev,
        sportCv: file
      }));
      setFileName(file.name);
      setErrors(prev => ({
        ...prev,
        sportCv: undefined
      }));
    }
  };

  const uploadToCloudinary = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
    formData.append('resource_type', 'raw'); // For PDFs and non-image files
    
    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/raw/upload`,
      {
        method: 'POST',
        body: formData
      }
    );
    
    const data = await response.json();
    if (data.error) {
      throw new Error(data.error.message || 'File upload failed');
    }
    return data.secure_url;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      let fileUrl = '';
      if (formData.sportCv) {
        fileUrl = await uploadToCloudinary(formData.sportCv);
      }

      const templateParams = {
        from_name: `${formData.name} ${formData.surname}`,
        name: formData.name,
        surname: formData.surname,
        date_of_birth: formData.dateOfBirth,
        city_of_residency: formData.cityOfResidency,
        prospettive: formData.prospettive,
        email: formData.email,
        phone: formData.phone,
        to_email: 'luvizzi99@gmail.com',
        sport_cv_filename: formData.sportCv?.name || '',
        sport_cv_url: fileUrl
      };

      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      setSubmitStatus('success');
      // Reset form
      setFormData({
        name: '',
        surname: '',
        dateOfBirth: '',
        cityOfResidency: '',
        sportCv: null,
        prospettive: '',
        email: '',
        phone: ''
      });
      setFileName('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('EmailJS Error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id='contact-form' className='pt-20'>
      <div className='mx-4 sm:mx-8 large:w-[1136px] large:mx-auto'>
        <h3 className='text-white font-custom-bold text-xl xsm:text-2xl sm:text-3xl desktop:text-4xl'>
          {formTexts.title}
        </h3>
        
        <form ref={formRef} onSubmit={handleSubmit} className='bg-[#1A1A1A] rounded-md p-4 sm:p-8 mt-4'>
          {/* Name and Surname - 2 columns on tablet+ */}
          <div className='grid grid-cols-1 tablet:grid-cols-2 gap-4'>
            <div>
              <label htmlFor='name' className='text-[#1FDF4B] font-bold text-sm mb-2 block text-left'>
                {formTexts.labels.name} *
              </label>
              <input
                type='text'
                id='name'
                name='name'
                value={formData.name}
                onChange={handleInputChange}
                className='w-full bg-[#121212] border border-[#474747] rounded-md p-3 text-white font text-sm focus:border-[#1FDF4B] focus:outline-none'
                placeholder={formTexts.placeholders.name}
              />
              {errors.name && <p className='text-red-500 text-xs mt-1'>{errors.name}</p>}
            </div>

            <div>
              <label htmlFor='surname' className='text-[#1FDF4B] font-bold text-sm mb-2 block text-left'>
                {formTexts.labels.surname} *
              </label>
              <input
                type='text'
                id='surname'
                name='surname'
                value={formData.surname}
                onChange={handleInputChange}
                className='w-full bg-[#121212] border border-[#474747] rounded-md p-3 text-white font text-sm focus:border-[#1FDF4B] focus:outline-none'
                placeholder={formTexts.placeholders.surname}
              />
              {errors.surname && <p className='text-red-500 text-xs mt-1'>{errors.surname}</p>}
            </div>
          </div>

          {/* Date of Birth and City - 2 columns on tablet+ */}
          <div className='grid grid-cols-1 tablet:grid-cols-2 gap-4 mt-4'>
            <div>
              <label htmlFor='dateOfBirth' className='text-[#1FDF4B] font-bold text-sm mb-2 block text-left'>
                {formTexts.labels.dateOfBirth} *
              </label>
              <input
                type='date'
                id='dateOfBirth'
                name='dateOfBirth'
                value={formData.dateOfBirth}
                onChange={handleInputChange}
                className='w-full bg-[#121212] border border-[#474747] rounded-md p-3 text-white font text-sm focus:border-[#1FDF4B] focus:outline-none'
              />
              {errors.dateOfBirth && <p className='text-red-500 text-xs mt-1'>{errors.dateOfBirth}</p>}
            </div>

            <div>
              <label htmlFor='cityOfResidency' className='text-[#1FDF4B] font-bold text-sm mb-2 block text-left'>
                {formTexts.labels.cityOfResidency} *
              </label>
              <input
                type='text'
                id='cityOfResidency'
                name='cityOfResidency'
                value={formData.cityOfResidency}
                onChange={handleInputChange}
                className='w-full bg-[#121212] border border-[#474747] rounded-md p-3 text-white font text-sm focus:border-[#1FDF4B] focus:outline-none'
                placeholder={formTexts.placeholders.cityOfResidency}
              />
              {errors.cityOfResidency && <p className='text-red-500 text-xs mt-1'>{errors.cityOfResidency}</p>}
            </div>
          </div>

          {/* Sport CV - File Upload */}
          <div className='mt-4'>
            <label htmlFor='sportCv' className='text-[#1FDF4B] font-bold text-sm mb-2 block text-left'>
              {formTexts.labels.sportCv} *
            </label>
            <div className='relative'>
              <input
                type='file'
                id='sportCv'
                name='sportCv'
                ref={fileInputRef}
                accept='.pdf,application/pdf'
                onChange={handleFileChange}
                className='absolute inset-0 w-full h-full opacity-0 cursor-pointer'
              />
              <div className='w-full bg-[#121212] border border-[#474747] rounded-md p-3 text-white font text-sm focus-within:border-[#1FDF4B] flex items-center justify-between'>
                <span className={fileName ? 'text-white' : 'text-gray-500'}>
                  {fileName || formTexts.placeholders.sportCv}
                </span>
                <div className='inline-flex items-center justify-center bg-[#1FDF4B] text-[#121212] px-3 py-1 rounded text-xs font-bold'>
                  {formTexts.buttons.browse}
                </div>
              </div>
            </div>
            {errors.sportCv && <p className='text-red-500 text-xs mt-1'>{errors.sportCv}</p>}
          </div>

          {/* Prospettive - Textarea */}
          <div className='mt-4'>
            <label htmlFor='prospettive' className='text-[#1FDF4B] font-bold text-sm mb-2 block text-left'>
              {formTexts.labels.prospettive} *
            </label>
            <textarea
              id='prospettive'
              name='prospettive'
              value={formData.prospettive}
              onChange={handleInputChange}
              className='w-full bg-[#121212] border border-[#474747] rounded-md p-3 text-white font text-sm focus:border-[#1FDF4B] focus:outline-none h-32 resize-none'
              placeholder={formTexts.placeholders.prospettive}
            />
            {errors.prospettive && <p className='text-red-500 text-xs mt-1'>{errors.prospettive}</p>}
          </div>

          {/* Email */}
          <div className='mt-4'>
            <label htmlFor='email' className='text-[#1FDF4B] font-bold text-sm mb-2 block text-left'>
              {formTexts.labels.email} *
            </label>
            <input
              type='email'
              id='email'
              name='email'
              value={formData.email}
              onChange={handleInputChange}
              className='w-full bg-[#121212] border border-[#474747] rounded-md p-3 text-white font text-sm focus:border-[#1FDF4B] focus:outline-none'
              placeholder={formTexts.placeholders.email}
            />
            {errors.email && <p className='text-red-500 text-xs mt-1'>{errors.email}</p>}
          </div>

          {/* Phone Number */}
          <div className='mt-4'>
            <label htmlFor='phone' className='text-[#1FDF4B] font-bold text-sm mb-2 block text-left'>
              {formTexts.labels.phone} *
            </label>
            <input
              type='tel'
              id='phone'
              name='phone'
              value={formData.phone}
              onChange={handleInputChange}
              className='w-full bg-[#121212] border border-[#474747] rounded-md p-3 text-white font text-sm focus:border-[#1FDF4B] focus:outline-none'
              placeholder={formTexts.placeholders.phone}
            />
            {errors.phone && <p className='text-red-500 text-xs mt-1'>{errors.phone}</p>}
          </div>

          {/* Submit Button */}
          <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between'>
            <button
              type='submit'
              disabled={isSubmitting}
              className='w-full sm:w-auto inline-flex items-center justify-center bg-[#1FDF4B] text-[#121212] font-bold px-8 py-3 rounded-md mt-6 hover:opacity-90 cursor-pointer disabled:opacity-50'
            >
              {isSubmitting ? formTexts.buttons.submitting : formTexts.buttons.submit}
            </button>

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <p className='text-[#1FDF4B] font text-sm mt-4 sm:mt-6'>
                {formTexts.messages.success}
              </p>
            )}
            {submitStatus === 'error' && (
              <p className='text-red-500 font text-sm mt-4 sm:mt-6'>
                {formTexts.messages.error}
              </p>
            )}
          </div>
        </form>
      </div>
    </section>
  );
};

export default ContactForm;